import React, { useState } from 'react';
import { Modal, Button, Select, Input, Radio, message } from 'antd';
import { Users } from 'lucide-react';
import { assignStudiesToUser } from '../../view/studies/StudiesService';

const { TextArea } = Input;
const { Option } = Select;

/* ── Figma-extracted design tokens ──────────────────────────────── */
const COLORS = {
  teal: '#3da295',
  tealHover: '#358c81',
  tealBg10: 'rgba(61,162,149,0.1)',
  tealBorder20: 'rgba(61,162,149,0.2)',
  white: '#ffffff',
  gray50: '#f9fafb',
  gray200: '#e5e7eb',
  gray300: '#d1d5db',
  gray400: '#9ca3af',
  gray500: '#6b7280',
  gray600: '#4b5563',
  gray700: '#374151',
  gray900: '#111827',
  border: 'rgba(0,0,0,0.1)',
  inputBg: '#f3f3f5',
  muted: '#717182',
};

const overlayStyle = {
  backgroundColor: 'rgba(0,0,0,0.5)',
};

const modalBodyStyle = {
  padding: 24,
};

const titleRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 4,
};

const titleTextStyle = {
  fontSize: 18,
  fontWeight: 600,
  lineHeight: 1,
  color: COLORS.gray900,
  margin: 0,
};

const descriptionStyle = {
  fontSize: 14,
  color: COLORS.gray600,
  margin: 0,
};

const sectionGap = { marginTop: 24 };

const noticeBannerStyle = {
  borderRadius: 8,
  background: COLORS.tealBg10,
  border: `1px solid ${COLORS.tealBorder20}`,
  padding: 16,
};

const noticeBannerTextStyle = {
  fontSize: 14,
  color: COLORS.gray700,
  margin: 0,
};

const sectionLabelStyle = {
  fontSize: 14,
  fontWeight: 600,
  color: COLORS.gray900,
  marginBottom: 12,
};

/* Priority cards */
const priorityGridStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 12,
};

const priorityCardBase = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: 12,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 8,
  cursor: 'pointer',
  transition: 'background 0.2s',
  background: COLORS.white,
};

const priorityTitleStyle = {
  fontWeight: 500,
  color: COLORS.gray900,
  fontSize: 14,
};

const priorityDescStyle = {
  fontSize: 12,
  color: COLORS.gray500,
};

/* Textarea */
const textareaStyle = {
  background: COLORS.inputBg,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 6,
  padding: '8px 12px',
  fontSize: 14,
  width: '100%',
  resize: 'none',
};

/* Footer */
const footerStyle = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 8,
  marginTop: 24,
};

/* Radiologist list item — removed hardcoded fallback users. Only real Keycloak users are used. */

export function BulkAssignDialog({ studyIds, studies, open, onOpenChange, onAssign, users }) {
  const [selectedRadiologist, setSelectedRadiologist] = useState('');
  const [priority, setPriority] = useState('normal');
  const [notes, setNotes] = useState('');
  const [assigning, setAssigning] = useState(false);

  const userList = users?.data || [];
  const usersLoading = userList.length === 0 && open;

  const handleAssign = async () => {
    if (!selectedRadiologist || !studyIds || studyIds.length === 0) {
      message.error('Please select a radiologist');
      return;
    }

    setAssigning(true);
    try {
      const studyUIDs = (studies || []).map(s => s.studyInstanceUID).filter(Boolean);

      await assignStudiesToUser({
        study_instance_uids: studyUIDs,
        assignee_id: selectedRadiologist,
        priority,
        notes,
      });

      const selected = userList.find(u => u.id === selectedRadiologist);
      const displayName = [selected?.first_name, selected?.last_name].filter(Boolean).join(' ') || selected?.username || selectedRadiologist;

      message.success(
        `${studyIds.length} ${studyIds.length === 1 ? 'study' : 'studies'} assigned to ${displayName}`
      );

      setSelectedRadiologist('');
      setPriority('normal');
      setNotes('');
      onOpenChange(false);
      if (onAssign) onAssign();
    } catch (error) {
      console.error('Bulk assign error:', error);
      message.error('Failed to assign studies. Please try again.');
    } finally {
      setAssigning(false);
    }
  };

  const handleClose = () => {
    setSelectedRadiologist('');
    setPriority('normal');
    setNotes('');
    onOpenChange(false);
  };

  const count = (studyIds || []).length;
  if (count === 0) return null;

  return (
    <Modal
      visible={open}
      onCancel={handleClose}
      footer={null}
      width={600}
      centered
      destroyOnClose
      maskStyle={overlayStyle}
      bodyStyle={modalBodyStyle}
    >
      {/* Header */}
      <div>
        <div style={titleRowStyle}>
          <Users size={20} color={COLORS.teal} />
          <h3 style={titleTextStyle}>Bulk Assign Studies</h3>
        </div>
        <p style={descriptionStyle}>
          Assigning {count} selected {count === 1 ? 'study' : 'studies'} to a radiologist for review and reporting.
        </p>
      </div>

      {/* Notice Banner */}
      <div style={{ ...sectionGap }}>
        <div style={noticeBannerStyle}>
          <p style={noticeBannerTextStyle}>
            <strong style={{ color: COLORS.teal }}>{count} {count === 1 ? 'study' : 'studies'}</strong>{' '}
            will be assigned with the same priority level and notes.
          </p>
        </div>
      </div>

      {/* Select Radiologist */}
      <div style={sectionGap}>
        <div style={sectionLabelStyle}>Select Radiologist</div>
        <Select
          value={selectedRadiologist || undefined}
          onChange={setSelectedRadiologist}
          placeholder={usersLoading ? 'Loading radiologists...' : userList.length === 0 ? 'No radiologists available' : 'Choose a radiologist'}
          style={{ width: '100%' }}
          size="large"
          dropdownStyle={{ borderRadius: 8 }}
          loading={usersLoading}
          disabled={userList.length === 0}
        >
          {userList.map((user) => (
              <Option key={user.id} value={user.id}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 500, fontSize: 14 }}>
                      {[user.first_name, user.last_name].filter(Boolean).join(' ') || user.username || user.id}
                    </div>
                    <div style={{ fontSize: 12, color: COLORS.gray500 }}>{user.email || ''}</div>
                  </div>
                </div>
              </Option>
            ))}
        </Select>
      </div>

      {/* Priority Level */}
      <div style={sectionGap}>
        <div style={sectionLabelStyle}>Priority Level</div>
        <div style={priorityGridStyle}>
          {[
            { value: 'stat', label: 'STAT', desc: 'Immediate attention' },
            { value: 'urgent', label: 'Urgent', desc: 'Within 24 hours' },
            { value: 'normal', label: 'Normal', desc: 'Standard workflow' },
            { value: 'routine', label: 'Routine', desc: 'No rush' },
          ].map((p) => (
            <div
              key={p.value}
              role="button"
              tabIndex={0}
              onClick={() => setPriority(p.value)}
              onKeyDown={(e) => e.key === 'Enter' && setPriority(p.value)}
              style={{
                ...priorityCardBase,
                background: priority === p.value ? COLORS.gray50 : COLORS.white,
                borderColor: priority === p.value ? COLORS.teal : COLORS.border,
              }}
            >
              <Radio checked={priority === p.value} />
              <div>
                <div style={priorityTitleStyle}>{p.label}</div>
                <div style={priorityDescStyle}>{p.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Additional Notes */}
      <div style={sectionGap}>
        <div style={sectionLabelStyle}>Additional Notes (Optional)</div>
        <TextArea
          placeholder="Add any special instructions or notes for the radiologist..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          style={textareaStyle}
        />
      </div>

      {/* Footer */}
      <div style={footerStyle}>
        <Button
          onClick={handleClose}
          style={{
            border: `1px solid ${COLORS.gray300}`,
            background: COLORS.white,
            color: COLORS.gray700,
            height: 36,
            padding: '0 16px',
            borderRadius: 6,
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          Cancel
        </Button>
        <Button
          type="primary"
          onClick={handleAssign}
          loading={assigning}
          style={{
            background: COLORS.teal,
            borderColor: COLORS.teal,
            color: COLORS.white,
            height: 36,
            padding: '0 16px',
            borderRadius: 6,
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          Assign All {count} {count === 1 ? 'Study' : 'Studies'}
        </Button>
      </div>
    </Modal>
  );
}
